import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Footprints,
    Mountain,
    Bike,
    ShoppingBag,
    Link as LinkIcon,
    Tag,
    Trophy,
    Megaphone,
    AlertCircle,
    Timer,
    Calculator,
    Calendar,
    MapPin,
    Activity,
    Zap,
    Flag,
    TrendingUp,
    User,
    Users,
    Info,
    HelpCircle,
    Settings,
    Search,
    Home,
    Star,
    Heart,
    Share,
    Download,
    Upload,
    ExternalLink,
    ChevronRight,
    ArrowRight,
    Play,
    Pause,
    RotateCcw,
} from "lucide-react";

import InfoBtnIg from "../../assets/images/Social-icons/social-ig-v2.webp";
import InfoBtnTea from "../../assets/images/Social-icons/social-tea-v2.webp";

import {
    fetchAllNews,
    trackButtonClick,
    fetchGroupRegistrations,
    fetchCalculatorButtons,
} from "../../services/googleSheetsService";
import NewsModal from "../../components/NewsModal/NewsModal";
import SpeechBubble from "../../components/SpeechBubble/SpeechBubble";

import MaioCardLogoBefore from "../../assets/images/maio-card-logo-before.png";
import MaioCardLogoAfter from "../../assets/images/maio-card-logo-after.png";

// 莫蘭迪色系配色表 (與 GroupRegistration.js 保持一致)
const getMorandiColor = (type) => {
    if (!type) return { bg: "#E0E0E0", text: "#555555" }; // Default grey

    const normalizedType = type.trim().toLowerCase();
    if (!normalizedType) return { bg: "#E0E0E0", text: "#555555" };

    const fixedColors = {
        路跑: { bg: "#9CAFB7", text: "#FFFFFF" }, // 藍灰
        越野跑: { bg: "#C4A484", text: "#FFFFFF" }, // 卡其 (大地色)
        三鐵: { bg: "#D4A5A5", text: "#FFFFFF" }, // 粉紅
        喵喵選物: { bg: "#e2bd73ff", text: "#FFFFFF" }, // 粉紅
        其他連結: { bg: "#66b990ff", text: "#FFFFFF" }, // 粉紅
    };

    if (fixedColors[normalizedType]) {
        return fixedColors[normalizedType];
    }

    const colors = [
        { bg: "#9CAFB7", text: "#FFFFFF" },
        { bg: "#B5A397", text: "#FFFFFF" },
        { bg: "#A6B5A4", text: "#FFFFFF" },
        { bg: "#D4A5A5", text: "#FFFFFF" },
        { bg: "#B6A6CA", text: "#FFFFFF" },
        { bg: "#8DA399", text: "#FFFFFF" },
        { bg: "#C4A484", text: "#FFFFFF" },
        { bg: "#9E97A8", text: "#FFFFFF" },
        { bg: "#8F9E8B", text: "#FFFFFF" },
        { bg: "#A89F91", text: "#FFFFFF" },
        { bg: "#C5B4A1", text: "#FFFFFF" },
        { bg: "#949494", text: "#FFFFFF" },
    ];

    let hash = 5381;
    for (let i = 0; i < normalizedType.length; i++) {
        hash = (hash << 5) + hash + normalizedType.charCodeAt(i);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
};

// 將 Hex 顏色轉為 RGBA
const hexToRgba = (hex, alpha) => {
    const cleanHex = hex.replace("#", "");
    let r, g, b;

    if (cleanHex.length === 6) {
        r = parseInt(cleanHex.substring(0, 2), 16);
        g = parseInt(cleanHex.substring(2, 4), 16);
        b = parseInt(cleanHex.substring(4, 6), 16);
    } else if (cleanHex.length === 8) {
        r = parseInt(cleanHex.substring(0, 2), 16);
        g = parseInt(cleanHex.substring(2, 4), 16);
        b = parseInt(cleanHex.substring(4, 6), 16);
    } else {
        return `rgba(224, 224, 224, ${alpha})`;
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getCategoryIcon = (type) => {
    const normalizedType = type?.trim().toLowerCase() || "";
    switch (normalizedType) {
        case "路跑":
            return <Footprints size={24} />;
        case "越野跑":
            return <Mountain size={24} />;
        case "三鐵":
            return <Bike size={24} />;
        case "喵喵選物":
            return <ShoppingBag size={24} />;
        case "其他連結":
            return <LinkIcon size={24} />;
        default:
            return <Tag size={24} />;
    }
};

const getNewsStyle = (category) => {
    const normalizedCategory = category?.trim() || "公告";
    switch (normalizedCategory) {
        case "賽事":
            return {
                icon: <Trophy size={24} />,
                color: "#d97706", // Amber 600
                bg: "#fef3c7", // Amber 100
            };
        case "優惠":
            return {
                icon: <ShoppingBag size={24} />,
                color: "#db2777", // Pink 600
                bg: "#fce7f3", // Pink 100
            };
        case "公告":
            return {
                icon: <Megaphone size={24} />,
                color: "#2563eb", // Blue 600
                bg: "#dbeafe", // Blue 100
            };
        case "重要":
            return {
                icon: <AlertCircle size={24} />,
                color: "#dc2626", // Red 600
                bg: "#fee2e2", // Red 100
            };
        default:
            return {
                icon: <Tag size={24} />,
                color: "#4b5563", // Gray 600
                bg: "#f3f4f6", // Gray 100
            };
    }
};

const IconMap = {
    Footprints,
    Mountain,
    Bike,
    ShoppingBag,
    Link: LinkIcon,
    Tag,
    Trophy,
    Megaphone,
    AlertCircle,
    Timer,
    Calculator,
    Calendar,
    MapPin,
    Activity,
    Zap,
    Flag,
    TrendingUp,
    User,
    Users,
    Info,
    HelpCircle,
    Settings,
    Search,
    Home,
    Star,
    Heart,
    Share,
    Download,
    Upload,
    ExternalLink,
    ChevronRight,
    ArrowRight,
    Play,
    Pause,
    RotateCcw,
};

// 中文名稱對應表
const IconNameMapping = {
    腳印: "Footprints",
    山: "Mountain",
    單車: "Bike",
    購物袋: "ShoppingBag",
    連結: "Link",
    標籤: "Tag",
    獎盃: "Trophy",
    大聲公: "Megaphone",
    警告: "AlertCircle",
    計時器: "Timer",
    計算機: "Calculator",
    日曆: "Calendar",
    地標: "MapPin",
    心跳: "Activity",
    閃電: "Zap",
    旗幟: "Flag",
    趨勢: "TrendingUp",
    使用者: "User",
    資訊: "Info",
    問號: "HelpCircle",
    設定: "Settings",
    搜尋: "Search",
    首頁: "Home",
    星星: "Star",
    愛心: "Heart",
    分享: "Share",
    下載: "Download",
    上傳: "Upload",
    外部連結: "ExternalLink",
    向右箭頭: "ArrowRight",
    播放: "Play",
    暫停: "Pause",
    重置: "RotateCcw",
};

const getDynamicIcon = (iconName) => {
    if (!iconName) return <Footprints size={32} />;

    const trimmedName = iconName.trim();

    // 1. 嘗試直接匹配 (Case-sensitive)
    if (IconMap[trimmedName]) {
        const IconComponent = IconMap[trimmedName];
        return <IconComponent size={32} />;
    }

    // 2. 嘗試中文 Mapping
    const mappedName = IconNameMapping[trimmedName];
    if (mappedName && IconMap[mappedName]) {
        const IconComponent = IconMap[mappedName];
        return <IconComponent size={32} />;
    }

    // 3. 嘗試 Case-insensitive 英文匹配
    const lowerName = trimmedName.toLowerCase();
    const foundKey = Object.keys(IconMap).find((key) => key.toLowerCase() === lowerName);
    if (foundKey) {
        const IconComponent = IconMap[foundKey];
        return <IconComponent size={32} />;
    }

    return <Footprints size={32} />;
};

// 骨架屏組件
const SkeletonButton = () => (
    <div className="index-button skeleton-loading" style={{ cursor: "default" }}>
        <div
            className="icon-box"
            style={{
                backgroundColor: "#e0e0e0",
                animation: "pulse 1.5s ease-in-out infinite",
            }}
        >
            <div style={{ width: "32px", height: "32px" }}></div>
        </div>
        <div
            style={{
                width: "60%",
                height: "20px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
            }}
        ></div>
    </div>
);

const SkeletonDashboardItem = () => (
    <div className="dashboard-item skeleton-loading" style={{ cursor: "default" }}>
        <div
            className="icon-wrapper"
            style={{
                backgroundColor: "#e0e0e0",
                borderRadius: "50%",
                animation: "pulse 1.5s ease-in-out infinite",
            }}
        >
            <div style={{ width: "24px", height: "24px" }}></div>
        </div>
        <div
            style={{
                width: "60%",
                height: "16px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
                marginTop: "8px",
            }}
        ></div>
        <div
            style={{
                width: "30px",
                height: "24px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
                marginTop: "4px",
            }}
        ></div>
    </div>
);

const SkeletonNewsItem = () => (
    <div className="news-item skeleton-loading" style={{ cursor: "default" }}>
        <div
            className="news-icon-wrapper"
            style={{
                backgroundColor: "#e0e0e0",
                borderRadius: "50%",
                animation: "pulse 1.5s ease-in-out infinite",
            }}
        >
            <div style={{ width: "24px", height: "24px" }}></div>
        </div>
        <div className="news-content-wrapper" style={{ flex: 1 }}>
            <div
                style={{
                    width: "80px",
                    height: "14px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                    animation: "pulse 1.5s ease-in-out infinite",
                    marginBottom: "8px",
                }}
            ></div>
            <div
                style={{
                    width: "70%",
                    height: "18px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                    animation: "pulse 1.5s ease-in-out infinite",
                    marginBottom: "8px",
                }}
            ></div>
            <div
                style={{
                    width: "90%",
                    height: "16px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                    animation: "pulse 1.5s ease-in-out infinite",
                }}
            ></div>
        </div>
        <div
            style={{
                width: "50px",
                height: "24px",
                backgroundColor: "#e0e0e0",
                borderRadius: "12px",
                animation: "pulse 1.5s ease-in-out infinite",
            }}
        ></div>
    </div>
);

function IndexPage() {
    const [newsList, setNewsList] = useState([
        {
            id: "1",
            date: "2025/11/18",
            title: "台北馬拉松加油",
            content: "台北馬拉松加油～🙌🙌🙌",
            category: "賽事",
            isArchived: false,
        },
    ]);
    const [isNewsLoading, setIsNewsLoading] = useState(true);
    const [isGroupLoading, setIsGroupLoading] = useState(true);
    const [isButtonsLoading, setIsButtonsLoading] = useState(true);
    const [selectedNews, setSelectedNews] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLogoHovered, setIsLogoHovered] = useState(false);
    const [groupCounts, setGroupCounts] = useState({});
    const [calculatorButtons, setCalculatorButtons] = useState([]);

    useEffect(() => {
        const loadNews = async () => {
            setIsNewsLoading(true);
            try {
                const allNews = await fetchAllNews();
                // 過濾掉已下架的新聞
                const activeNews = allNews.filter((news) => !news.isArchived);
                setNewsList(activeNews);
            } catch (error) {
                console.error("Failed to load news:", error);
                // 保持預設值
            } finally {
                setIsNewsLoading(false);
            }
        };

        const loadGroupCounts = async () => {
            setIsGroupLoading(true);
            try {
                const data = await fetchGroupRegistrations();
                const counts = {};
                data.forEach((item) => {
                    // 如果已下架，則不計入
                    if (item.isArchived) return;

                    const type = item.type || "其他";
                    counts[type] = (counts[type] || 0) + 1;
                });
                setGroupCounts(counts);
            } catch (error) {
                console.error("Failed to load group counts:", error);
            } finally {
                setIsGroupLoading(false);
            }
        };

        const loadCalculatorButtons = async () => {
            setIsButtonsLoading(true);
            try {
                const buttons = await fetchCalculatorButtons();
                setCalculatorButtons(buttons);
            } catch (error) {
                console.error("Failed to load calculator buttons:", error);
            } finally {
                setIsButtonsLoading(false);
            }
        };

        loadNews();
        loadGroupCounts();
        loadCalculatorButtons();
    }, []);

    const handleNewsClick = (news) => {
        // 記錄點擊事件，使用消息內容作為按鈕名稱
        const newsTitle =
            news.title ||
            (news.content.length > 30 ? `${news.content.substring(0, 30)}...` : news.content);
        trackButtonClick(`首頁-最新消息：${newsTitle}`, `news-${news.id}`);
        setSelectedNews(news);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedNews(null), 300);
    };

    const handleButtonClick = (buttonName, buttonPath) => {
        trackButtonClick(buttonName, buttonPath);
    };

    return (
        <section className="content-section">
            {/* PC 左邊區塊 */}
            <div className="main-first-wrap">
                {/* 主視覺區 */}
                <div className="main-info-wrap">
                    <div
                        className="index-img-wrap"
                        onMouseEnter={() => setIsLogoHovered(true)}
                        onMouseLeave={() => setIsLogoHovered(false)}
                        onClick={() => handleButtonClick("首頁-奶茶喵喵頭像", "無連結")}
                    >
                        <img
                            src={isLogoHovered ? MaioCardLogoAfter : MaioCardLogoBefore}
                            alt="貓貓問個痛主視覺"
                        />
                        <SpeechBubble text="喵！" position="top-main-card" />
                    </div>
                    <div className="index-title-wrap">
                        <h2>喵喵問個痛</h2>
                        <p>一隻任性的喵立志長成鯨魚喵</p>
                    </div>
                    <div className="social-links-wrap">
                        <a
                            href="https://www.instagram.com/memoryflying"
                            className="social-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            title=""
                            onClick={() =>
                                handleButtonClick(
                                    "首頁-Instagram按鈕",
                                    "https://www.instagram.com/memoryflying"
                                )
                            }
                        >
                            <SpeechBubble text="喵！" position="top" align="right" />
                            <div className="social-links-img-wrap">
                                <img src={InfoBtnIg} alt="Instagram" />
                            </div>
                        </a>
                        <a
                            href="https://catvv.bobaboba.me/"
                            className="social-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            title=""
                            onClick={() =>
                                handleButtonClick(
                                    "首頁-喵喵奶茶贊助按鈕",
                                    "https://catvv.bobaboba.me/"
                                )
                            }
                        >
                            <SpeechBubble text="喵！" position="top" align="right" />
                            <div className="social-links-img-wrap">
                                <img src={InfoBtnTea} alt="喵喵奶茶贊助按鈕" />
                            </div>
                        </a>
                    </div>
                </div>
                {/* 熱門服務 */}
                <div className="index-button-wrap">
                    <h3 className="service-title">喵喵任意門</h3>
                    {isButtonsLoading && calculatorButtons.length === 0 ? (
                        // 初次載入：根據預期數量顯示骨架屏
                        <>
                            {Array.from({ length: 4 }).map((_, index) => (
                                <SkeletonButton key={`skeleton-btn-${index}`} />
                            ))}
                        </>
                    ) : calculatorButtons.length === 0 && !isButtonsLoading ? (
                        // 沒有資料時顯示提示
                        <div className="empty-state">
                            <p>目前沒有可用的服務</p>
                        </div>
                    ) : (
                        calculatorButtons.map((btn) => {
                            const isExternal = btn.link.startsWith("http") || btn.link.startsWith("//");
                            const commonProps = {
                                key: btn.id,
                                className: "index-button",
                                onClick: () =>
                                    handleButtonClick(`首頁-喵喵任意門-${btn.title}`, btn.link),
                            };

                            const content = (
                                <>
                                    <SpeechBubble text="喵！" position="top" align="right" />
                                    <div className="icon-box">{getDynamicIcon(btn.icon)}</div>
                                    {btn.title}
                                </>
                            );

                            return isExternal ? (
                                <a
                                    {...commonProps}
                                    href={btn.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {content}
                                </a>
                            ) : (
                                <Link {...commonProps} to={btn.link}>
                                    {content}
                                </Link>
                            );
                        })
                    )}
                </div>
            </div>

            {/* 右邊區塊 */}
            <div className="main-second-wrap">
                {/* 好康儀表板 */}
                <div className="goodies-dashboard-wrap">
                    <div className="dashboard-header">
                        <h2>好康儀表板</h2>
                        <Link
                            to="/GroupRegistration"
                            className="see-more-btn"
                            onClick={() =>
                                handleButtonClick("首頁-好康儀表板-看更多", "/GroupRegistration")
                            }
                        >
                            看更多 →
                        </Link>
                    </div>
                    <div className="dashboard-grid">
                        {isGroupLoading && Object.keys(groupCounts).length === 0 ? (
                            // 初次載入：顯示 4 個骨架屏（常見的類別數量）
                            <>
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <SkeletonDashboardItem key={`skeleton-dashboard-${index}`} />
                                ))}
                            </>
                        ) : Object.keys(groupCounts).length === 0 && !isGroupLoading ? (
                            // 沒有資料時顯示提示
                            <div className="empty-state" style={{ gridColumn: "1 / -1" }}>
                                <p>目前沒有好康活動</p>
                            </div>
                        ) : (
                            Object.entries(groupCounts).map(([category, count]) => {
                                const colorStyle = getMorandiColor(category);
                                const bgLight = hexToRgba(colorStyle.bg, 0.15);
                                const bgHover = hexToRgba(colorStyle.bg, 0.25);
                                const borderLight = hexToRgba(colorStyle.bg, 0.3);

                                return (
                                    <Link
                                        key={category}
                                        to={`/GroupRegistration?category=${encodeURIComponent(
                                            category
                                        )}`}
                                        className="dashboard-item"
                                        style={{
                                            "--theme-color": colorStyle.bg,
                                            "--bg-light": bgLight,
                                            "--bg-hover": bgHover,
                                            "--border-light": borderLight,
                                        }}
                                        onClick={() =>
                                            handleButtonClick(
                                                `首頁-好康儀表板-${category}`,
                                                `/GroupRegistration?category=${category}`
                                            )
                                        }
                                    >
                                        <SpeechBubble text="喵！" position="top" align="center" />
                                        <div className="icon-wrapper" style={{ color: colorStyle.bg }}>
                                            {getCategoryIcon(category)}
                                        </div>
                                        <span className="category-name">{category}</span>
                                        <span
                                            className="category-count"
                                            style={{ color: colorStyle.bg }}
                                        >
                                            {count}
                                        </span>
                                    </Link>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* 最新消息 */}
                <div className={`news-wrap ${isNewsLoading ? "loading" : ""}`}>
                    <h2>最新消息</h2>
                    <div className="news-list-container">
                        <div className="news-list">
                            {isNewsLoading && newsList.length <= 1 ? (
                                // 初次載入或只有預設資料：顯示 3 個骨架屏
                                <>
                                    {Array.from({ length: 3 }).map((_, index) => (
                                        <SkeletonNewsItem key={`skeleton-news-${index}`} />
                                    ))}
                                </>
                            ) : newsList.length === 0 && !isNewsLoading ? (
                                // 沒有資料時顯示提示
                                <div className="empty-state">
                                    <p>目前沒有最新消息</p>
                                </div>
                            ) : (
                                newsList.map((news) => {
                                    const style = getNewsStyle(news.category);
                                    return (
                                        <div
                                            key={news.id}
                                            className="news-item"
                                            onClick={() => handleNewsClick(news)}
                                            style={{
                                                "--news-color": style.color,
                                                "--news-bg": style.bg,
                                            }}
                                        >
                                            <SpeechBubble text="喵！" position="top" align="right" />
                                            <div className="news-icon-wrapper">{style.icon}</div>
                                            <div className="news-content-wrapper">
                                                <p className="news-date">{news.date}</p>
                                                <h3 className="news-title">{news.title}</h3>
                                                <p className="news-content">{news.content}</p>
                                            </div>
                                            <span className="news-tag">{news.category || "公告"}</span>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <NewsModal isOpen={isModalOpen} onClose={handleCloseModal} news={selectedNews} />
        </section>
    );
}

export default IndexPage;
